import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CardDiscardReasonEnum, ConfirmationComponent, DISCARD_REASON_LABEL, LossReason, LossReasonService, Pager } from 'lib-trend-core';
import { debounceTime, distinctUntilChanged, finalize, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { LossReasonFormModalComponent } from './loss-reason-form-modal/loss-reason-form-modal.component';

@Component({
    selector: 'app-loss-reason',
    templateUrl: './loss-reason.component.html',
    styleUrl: './loss-reason.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LossReasonComponent extends AbstractComponent {
  public pager: Pager<LossReason> = new Pager<LossReason>({ perPage: 10 });
  listObservable: Observable<Pager<LossReason>>;
  private searchTerm: Subject<string> = new Subject<string>();

  /** E3 RF-36 (ADR-001): offered to every company, never stored, never editable — so listed from the enum, not the pager. */
  readonly platformReasons = Object.values(CardDiscardReasonEnum);
  readonly DISCARD_REASON_LABEL = DISCARD_REASON_LABEL;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private lossReasonService: LossReasonService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getList();
  }

  getList() {
    this.loadingContent = true;

    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    this.lossReasonService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingContent = false)
      )
      .subscribe({
        next: (pager: Pager<LossReason>) => {
          this.pager = pager;
          this.setupObservableSearch();
        },
      });
  }

  search(termo: string) {
    this.pager.page = 1;
    this.searchString = termo;
    this.searchTerm.next(termo);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  add(): void {
    this.dialog.open(LossReasonFormModalComponent, {
      width: '1000px',
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: LossReason) => {
        this.getList();
      });
  }

  edit(lr: LossReason): void {
    this.dialog.open(LossReasonFormModalComponent, {
      width: '1000px',
      data: lr
    })
      .afterClosed().subscribe(result => {
        this.getList();
      });
  }

  delete(lr: LossReason): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });

    dialogRef.afterClosed()
      .subscribe((result: boolean) => {
        if (Boolean(result) === true) {
          this.lossReasonService.delete(lr._id)
            .pipe(takeUntil(this.destroy$)).subscribe({
              next: () => {
                this.alertService.success(this.t('kanban.lossReason.deleteSuccess'));
                this.getList();
              },
              error: (error) => { this.alertService.error(error.error.message) }
            });
        }
      });
  }


  setupObservableSearch() {
    this.listObservable = this.searchTerm
      .pipe(takeUntil(this.destroy$))
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(switchMap(term => {
        return this.lossReasonService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams);
      }));

    this.listObservable.subscribe((pager: Pager<LossReason>) => {
      this.pager = pager;
    });
  }
}
